import { createApi } from '@reduxjs/toolkit/dist/query/react';

import type { FlowStep, OnboardingPath } from '@app/features/quick-start/data';

import fetchBaseQuery from '../fetchBaseQuery';

export type QuickStartPayload = {
    stepName: FlowStep;
};

export type QuickStartResponseStep = {
    stepName: FlowStep;
    completed: boolean;
    skipped: boolean;
};

export type QuickStartResponse = {
    activeStep: FlowStep;
    completedAt: string | null;
    finished: boolean;
    userBaseProjectId?: string;
    path: OnboardingPath;
    steps: QuickStartResponseStep[];
};

const quickStartApi = createApi({
    reducerPath: 'quickStart',
    baseQuery: fetchBaseQuery(),
    refetchOnMountOrArgChange: true,
    tagTypes: ['onboardingStatus'],
    endpoints: (builder) => ({
        getOnboardingStatus: builder.query<QuickStartResponse, void>({
            query: () => ({
                method: 'GET',
                url: `/quick-start/status`,
            }),
            providesTags: ['onboardingStatus'],
        }),
        completeOnboardingStep: builder.mutation<QuickStartResponse, QuickStartPayload>({
            query: (payload) => ({
                url: `/quick-start/complete`,
                method: 'POST',
                body: payload,
            }),
            async onQueryStarted(payload, { dispatch, queryFulfilled }) {
                const { data } = await queryFulfilled;

                dispatch(
                    quickStartApi.util.updateQueryData('getOnboardingStatus', undefined, (draft) => {
                        Object.assign(draft, data);
                    }),
                );
            },
        }),
        skipOnboardingStep: builder.mutation<QuickStartResponse, QuickStartPayload>({
            query: (payload) => ({
                url: `/quick-start/skip`,
                method: 'POST',
                body: payload,
            }),
            // to reuse this function require to add long and complicated types
            async onQueryStarted(payload, { dispatch, queryFulfilled }) {
                const { data } = await queryFulfilled;

                dispatch(
                    quickStartApi.util.updateQueryData('getOnboardingStatus', undefined, (draft) => {
                        Object.assign(draft, data);
                    }),
                );
            },
        }),
        completeOnboarding: builder.mutation<QuickStartResponse, void>({
            query: () => ({
                url: `/quick-start/complete-onboarding`,
                method: 'POST',
            }),
        }),
    }),
});

export const {
    useCompleteOnboardingStepMutation,
    useSkipOnboardingStepMutation,
    useGetOnboardingStatusQuery,
    useCompleteOnboardingMutation,
} = quickStartApi;

export const onboardingMiddleware = quickStartApi.middleware;

export default quickStartApi;
